import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import athleteEventAPI from '../api/athleteEvent.api';
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Stack,
  CircularProgress,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Visibility,
  FilterList,
  Add,
  Edit,
  Delete,
  Close,
  Event as EventIcon,
  CalendarToday,
  LocationOn,
  Person,
  Email,
  Phone,
  Image as ImageIcon,
  Public,
  Group,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';

const AthleteEventsList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editEventId, setEditEventId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stateAssociations, setStateAssociations] = useState([]);
  const [pagination, setPagination] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  const [filters, setFilters] = useState({
    status: 'all',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    lastDateOfApplication: '',
    venue: '',
    contactPersonName: '',
    contactPersonEmail: '',
    contactPersonMobile: '',
    stateAssociations: [],
    isOpenToAll: false,
    status: 'published',
    coverImage: null,
  });

  // Helper: today in yyyy-mm-dd
  const getTodayDateString = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString().split('T')[0];
  };

  const todayStr = getTodayDateString();

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchEvents();
    fetchStateAssociations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdmin, navigate, filters]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await athleteEventAPI.getAllAthleteEvents(filters);
      setEvents(response.data || []);
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Fetch events error:', error);
      toast.error(error.response?.data?.message || 'Failed to load athlete events');
      setEvents([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  const fetchStateAssociations = async () => {
    try {
      const response = await athleteEventAPI.getApprovedStateAssociations();
      setStateAssociations(response.data || []);
    } catch (error) {
      console.error('Failed to load state associations:', error);
      setStateAssociations([]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchInput, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    if (key === 'page' || key === 'limit') {
      setFilters({ ...filters, [key]: value });
    } else {
      setFilters({ ...filters, [key]: value, page: 1 });
    }
  };

  const handleViewDetails = async (eventId) => {
    try {
      const response = await athleteEventAPI.getAthleteEventById(eventId);
      setSelectedEvent(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('View details error:', error);
      toast.error(error.response?.data?.message || 'Failed to load event details');
    }
  };

  const handleOpenCreateModal = () => {
    setEditMode(false);
    setEditEventId(null);
    setFormData({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      lastDateOfApplication: '',
      venue: '',
      contactPersonName: '',
      contactPersonEmail: '',
      contactPersonMobile: '',
      stateAssociations: [],
      isOpenToAll: false,
      status: 'published',
      coverImage: null,
    });
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (event) => {
    setEditMode(true);
    setEditEventId(event._id);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      startDate: event.startDate ? new Date(event.startDate).toISOString().split('T')[0] : '',
      endDate: event.endDate ? new Date(event.endDate).toISOString().split('T')[0] : '',
      lastDateOfApplication: event.lastDateOfApplication
        ? new Date(event.lastDateOfApplication).toISOString().split('T')[0]
        : '',
      venue: event.venue || '',
      contactPersonName: event.contactPerson?.name || '',
      contactPersonEmail: event.contactPerson?.email || '',
      contactPersonMobile: event.contactPerson?.mobile || '',
      stateAssociations: event.stateAssociations?.map((sa) => sa._id) || [],
      isOpenToAll: !!event.isOpenToAll,
      status: event.status || 'published',
      coverImage: null,
    });
    setShowCreateModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Interdependent date fields
    if (name === 'startDate') {
      const newStart = value;
      let newEnd = formData.endDate;
      let newLast = formData.lastDateOfApplication;

      // endDate must be on/after startDate
      if (newEnd && new Date(newEnd) < new Date(newStart)) {
        newEnd = newStart;
      }

      // lastDateOfApplication = startDate - 1 day
      if (newStart) {
        const d = new Date(newStart);
        d.setDate(d.getDate() - 1);
        newLast = d.toISOString().split('T')[0];
      } else {
        newLast = '';
      }

      setFormData({
        ...formData,
        startDate: newStart,
        endDate: newEnd,
        lastDateOfApplication: newLast,
      });
      return;
    }

    if (name === 'endDate') {
      setFormData({ ...formData, endDate: value });
      return;
    }

    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should not exceed 5MB');
        return;
      }
      setFormData({ ...formData, coverImage: file });
    }
  };

  const handleStateAssociationChange = (e) => {
    setFormData({ ...formData, stateAssociations: e.target.value });
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error('Event title is required');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error('Event description is required');
      return false;
    }
    if (!formData.startDate) {
      toast.error('Start date is required');
      return false;
    }
    if (new Date(formData.startDate) < new Date(todayStr)) {
      toast.error('Start date cannot be in the past');
      return false;
    }
    if (!formData.endDate) {
      toast.error('End date is required');
      return false;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error('End date cannot be before start date');
      return false;
    }
    if (!formData.lastDateOfApplication) {
      toast.error('Last date of application is required');
      return false;
    }
    if (new Date(formData.lastDateOfApplication) >= new Date(formData.startDate)) {
      toast.error('Last date of application must be before start date');
      return false;
    }
    if (!formData.venue.trim()) {
      toast.error('Venue is required');
      return false;
    }
    if (!formData.contactPersonName.trim()) {
      toast.error('Contact person name is required');
      return false;
    }
    if (!formData.contactPersonEmail.trim()) {
      toast.error('Contact person email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.contactPersonEmail)) {
      toast.error('Invalid contact person email');
      return false;
    }
    if (!formData.contactPersonMobile.trim()) {
      toast.error('Contact person mobile is required');
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(formData.contactPersonMobile)) {
      toast.error('Invalid contact person mobile number');
      return false;
    }
    if (!formData.isOpenToAll && formData.stateAssociations.length === 0) {
      toast.error('Please select at least one state association or mark as open to all');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('startDate', formData.startDate);
      data.append('endDate', formData.endDate);
      data.append('lastDateOfApplication', formData.lastDateOfApplication);
      data.append('venue', formData.venue);
      data.append('contactPersonName', formData.contactPersonName);
      data.append('contactPersonEmail', formData.contactPersonEmail);
      data.append('contactPersonMobile', formData.contactPersonMobile);
      data.append('isOpenToAll', formData.isOpenToAll);
      data.append('status', formData.status || 'published');

      if (!formData.isOpenToAll) {
        formData.stateAssociations.forEach((id) => data.append('stateAssociations[]', id));
      }

      if (formData.coverImage) {
        data.append('coverImage', formData.coverImage);
      }

      if (editMode) {
        await athleteEventAPI.updateAthleteEvent(editEventId, data);
        toast.success('Event updated successfully');
      } else {
        await athleteEventAPI.createAthleteEvent(data);
        toast.success('Event created successfully');
      }

      setShowCreateModal(false);
      fetchEvents();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!deleteEventId) return;

    setDeleting(true);
    try {
      await athleteEventAPI.deleteAthleteEvent(deleteEventId);
      toast.success('Event deleted successfully');
      setShowDeleteConfirm(false);
      setDeleteEventId(null);
      fetchEvents();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const handleImageView = (imageUrl, title) => {
    setCurrentImage({ url: imageUrl, title });
    setShowImageModal(true);
  };

  const handleImageDownload = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      published: 'success',
      cancelled: 'error',
      completed: 'info',
    };
    return colors[status] || 'default';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading && events.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 2 }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white' }}>
                Athlete Events List
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                Manage athlete events and competitions
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenCreateModal}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              Create Event
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Search Bar - Full Width */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by event title, venue, or description..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setSearchInput('');
                      setFilters({ ...filters, search: '', page: 1 });
                    }}
                  >
                    <Close />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Filter Section */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterList sx={{ mr: 1 }} />
              Filters
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {/* Status */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date From"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date To"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Clear Filters Button */}
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setFilters({
                      status: 'all',
                      startDate: '',
                      endDate: '',
                      search: '',
                      page: 1,
                      limit: 20,
                      sortBy: 'createdAt',
                      sortOrder: 'desc',
                    });
                    setSearchInput('');
                  }}
                  sx={{ height: '40px' }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons Section */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {/* Search/Apply Filters Button */}
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  fullWidth
                  sx={{ height: '44px' }}
                >
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Results Summary */}
        {pagination.totalRecords !== undefined && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {events.length} of {pagination.totalRecords} event(s)
            {filters.search && ` matching "${filters.search}"`}
          </Typography>
        )}

        {/* Events Table */}
        <Paper elevation={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Venue</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Accessibility</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No events found
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenCreateModal}
                        sx={{ mt: 2 }}
                      >
                        Create Event
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((ev) => (
                    <TableRow key={ev._id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ev.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ev.description?.slice(0, 80) || ''}
                          {ev.description && ev.description.length > 80 ? '…' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(ev.startDate)}</TableCell>
                      <TableCell>{formatDate(ev.endDate)}</TableCell>
                      <TableCell>{formatDate(ev.lastDateOfApplication)}</TableCell>
                      <TableCell>{ev.venue || '-'}</TableCell>
                      <TableCell>
                        {ev.isOpenToAll ? (
                          <Chip icon={<Public />} label="Open to All" color="success" size="small" />
                        ) : (
                          <Chip
                            icon={<Group />}
                            label={`${ev.stateAssociations?.length || 0} States`}
                            color="primary"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={ev.status} color={getStatusColor(ev.status)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Details">
                            <IconButton size="small" color="primary" onClick={() => handleViewDetails(ev._id)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" color="info" onClick={() => handleOpenEditModal(ev)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeleteEventId(ev._id);
                                setShowDeleteConfirm(true);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={pagination.totalRecords || 0}
            page={(filters.page || 1) - 1}
            onPageChange={(e, newPage) => handleFilterChange('page', newPage + 1)}
            rowsPerPage={filters.limit}
            onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>
      </Container>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Event' : 'Create New Event'}
          <IconButton
            onClick={() => setShowCreateModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title *"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter event title"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EventIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description *"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter event description"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date *"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayStr }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date *"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.startDate || todayStr }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Last Date of Application *"
                name="lastDateOfApplication"
                value={formData.lastDateOfApplication}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: formData.startDate || todayStr }}
                helperText="Auto-calculated as 1 day before start date"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status *</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Status *"
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Venue *"
                name="venue"
                value={formData.venue}
                onChange={handleInputChange}
                placeholder="Enter event venue"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Contact Person Details
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Person Name *"
                name="contactPersonName"
                value={formData.contactPersonName}
                onChange={handleInputChange}
                placeholder="Enter contact person name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person Email *"
                name="contactPersonEmail"
                type="email"
                value={formData.contactPersonEmail}
                onChange={handleInputChange}
                placeholder="email@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person Mobile *"
                name="contactPersonMobile"
                value={formData.contactPersonMobile}
                onChange={handleInputChange}
                placeholder="10-digit mobile number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    name="isOpenToAll"
                    checked={formData.isOpenToAll}
                    onChange={handleInputChange}
                  />
                }
                label="Open to All State Associations"
              />
            </Grid>

            {!formData.isOpenToAll && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select State Associations *</InputLabel>
                  <Select
                    multiple
                    value={formData.stateAssociations}
                    onChange={handleStateAssociationChange}
                    label="Select State Associations *"
                    renderValue={(selected) => {
                      const names = stateAssociations
                        .filter((sa) => selected.includes(sa._id))
                        .map((sa) => sa.registrationNumber);
                      return names.join(', ');
                    }}
                  >
                    {stateAssociations.map((sa) => (
                      <MenuItem key={sa._id} value={sa._id}>
                        <Checkbox checked={formData.stateAssociations.indexOf(sa._id) > -1} />
                        {sa.registrationNumber} - {sa.president?.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Only approved state associations are shown</FormHelperText>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth startIcon={<ImageIcon />}>
                Upload Cover Image
                <input type="file" hidden accept="image/*" onChange={handleFileChange} />
              </Button>
              {formData.coverImage && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {formData.coverImage.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={saving ? <CircularProgress size={20} /> : null}
            disabled={saving}
          >
            {saving ? 'Saving...' : editMode ? 'Update Event' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Event Details
          <IconButton onClick={() => setShowModal(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEvent && (
            <Box>
              {selectedEvent.coverImage?.url && (
                <Box
                  component="img"
                  src={selectedEvent.coverImage.url}
                  alt={selectedEvent.title}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: 300,
                    objectFit: 'cover',
                    borderRadius: 1,
                    mb: 3,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleImageView(selectedEvent.coverImage.url, selectedEvent.title)}
                />
              )}

              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {selectedEvent.title}
              </Typography>

              <Chip label={selectedEvent.status} color={getStatusColor(selectedEvent.status)} sx={{ mb: 2 }} />

              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                {selectedEvent.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dates
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    <strong>Start Date:</strong> {formatDate(selectedEvent.startDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    <strong>End Date:</strong> {formatDate(selectedEvent.endDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    <strong>Last Date to Apply:</strong> {formatDate(selectedEvent.lastDateOfApplication)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Venue
              </Typography>
              <Typography variant="body2" paragraph>
                <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {selectedEvent.venue}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Contact Person
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {selectedEvent.contactPerson?.name || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {selectedEvent.contactPerson?.email || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Mobile:</strong> {selectedEvent.contactPerson?.mobile || '-'}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Accessibility
              </Typography>
              {selectedEvent.isOpenToAll ? (
                <Chip icon={<Public />} label="Open to All State Associations" color="success" />
              ) : (
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Selected State Associations ({selectedEvent.stateAssociations?.length || 0})
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    {selectedEvent.stateAssociations?.map((sa) => (
                      <Chip key={sa._id} label={sa.registrationNumber} size="small" sx={{ mb: 1 }} />
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Meta
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Created: {formatDate(selectedEvent.createdAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Updated: {formatDate(selectedEvent.updatedAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Delete
          <IconButton
            onClick={() => setShowDeleteConfirm(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this event?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            ⚠️ This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteEvent}
            color="error"
            variant="contained"
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog open={showImageModal} onClose={() => setShowImageModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentImage?.title || 'Preview'}
          <IconButton onClick={() => setShowImageModal(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {currentImage && (
            <Box
              component="img"
              src={currentImage.url}
              alt={currentImage.title}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ImageIcon />}
            onClick={() => handleImageDownload(currentImage?.url, currentImage?.title)}
          >
            Download
          </Button>
          <Button onClick={() => setShowImageModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AthleteEventsList;
